import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle2, Factory, TrendingUp, Settings2, Leaf, PenTool, Wrench, ShieldCheck, Settings } from 'lucide-react';
export function ProcessCooling() {
  return <div className="pt-24 bg-white" data-id="element-843">
      {/* Top Summary */}
      <section className="py-16 md:py-24 bg-primary-light" data-id="element-844">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-845">
          <div className="max-w-3xl" data-id="element-846">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="inline-flex items-center space-x-2 bg-white px-3 py-1 rounded-full text-sm font-medium text-primary-dark mb-6" data-id="element-847">
              <Factory className="w-4 h-4 text-accent" data-id="element-848" />
              <span data-id="element-849">Industrial / Process Environment</span>
            </motion.div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-4xl md:text-5xl font-bold text-primary-dark mb-6" data-id="element-850">
              A cooling solution that supported operations and paid back fast
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="text-xl text-gray-700" data-id="element-851">
              Practical application-focused cooling that helped improve process
              control and reduce waste in production.
            </motion.p>
          </div>
        </div>
      </section>

      {/* Quick Facts Row */}
      <section className="py-12 border-b border-gray-200" data-id="element-852">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-853">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6" data-id="element-854">
            {[{
            label: 'Sector',
            value: 'Industrial'
          }, {
            label: 'Service',
            value: 'Process Cooling'
          }, {
            label: 'System',
            value: 'Bespoke Ducted'
          }, {
            label: 'Site',
            value: 'Production Facility'
          }, {
            label: 'Maintenance',
            value: 'Comprehensive'
          }, {
            label: 'Location',
            value: 'Midlands, UK'
          }].map((fact, index) => <div key={index} className="bg-surface p-4 rounded-2xl" data-id="element-855">
                <p className="text-sm text-gray-500 mb-1" data-id="element-856">{fact.label}</p>
                <p className="font-semibold text-primary-dark" data-id="element-857">{fact.value}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Challenge & Approach */}
      <section className="py-16 md:py-24" data-id="element-858">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-859">
          <div className="grid md:grid-cols-2 gap-16" data-id="element-860">
            <div data-id="element-861">
              <h2 className="text-3xl font-bold text-primary-dark mb-6" data-id="element-862">
                The Challenge
              </h2>
              <p className="text-gray-700 text-lg leading-relaxed" data-id="element-863">
                A manufacturing client required precise climate control within
                their production facility to ensure process quality. Fluctuating
                temperatures were causing inconsistencies in product curing
                times, leading to increased waste and operational
                inefficiencies. They needed a heavy-duty cooling solution
                capable of maintaining strict environmental parameters despite
                the heat generated by industrial machinery.
              </p>
            </div>
            <div data-id="element-864">
              <h2 className="text-3xl font-bold text-primary-dark mb-6" data-id="element-865">
                Our Approach
              </h2>
              <ul className="space-y-4" data-id="element-866">
                {['In-depth thermal assessment of the production floor', 'Design of a bespoke ducted cooling solution tailored to the machinery layout', 'Selection of high-capacity industrial-grade equipment', 'Strategic installation to avoid interference with production lines', 'Rigorous commissioning and calibration to exact process requirements'].map((item, index) => <li key={index} className="flex items-start" data-id="element-867">
                    <CheckCircle2 className="w-6 h-6 text-accent flex-shrink-0 mt-1 mr-3" data-id="element-868" />
                    <span className="text-gray-700 text-lg" data-id="element-869">{item}</span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* The Result */}
      <section className="py-16 md:py-24 bg-surface" data-id="element-870">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-871">
          <h2 className="text-3xl font-bold text-primary-dark mb-12 text-center" data-id="element-872">
            The Result
          </h2>
          <div className="grid md:grid-cols-3 gap-8" data-id="element-873">
            {[{
            icon: TrendingUp,
            title: 'Operational ROI',
            desc: 'Significant reduction in product waste, delivering a fast return on the system investment.'
          }, {
            icon: Settings2,
            title: 'Process Control',
            desc: 'Stabilized curing times and improved overall product consistency.'
          }, {
            icon: Leaf,
            title: 'Reduced Waste',
            desc: 'Lowered environmental impact through decreased material spoilage and efficient cooling.'
          }].map((result, index) => <div key={index} className="bg-white p-8 rounded-3xl shadow-sm" data-id="element-874">
                <div className="w-12 h-12 bg-primary-light rounded-xl flex items-center justify-center mb-6" data-id="element-875">
                  <result.icon className="w-6 h-6 text-accent" data-id="element-876" />
                </div>
                <h3 className="text-xl font-bold text-primary-dark mb-3" data-id="element-877">
                  {result.title}
                </h3>
                <p className="text-gray-600" data-id="element-878">{result.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* Visual Proof */}
      <section className="py-16 md:py-24" data-id="element-879">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-880">
          <div className="rounded-3xl overflow-hidden aspect-video relative bg-gray-200" data-id="element-881">
            <img src="https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?auto=format&fit=crop&q=80" alt="Industrial process cooling facility" className="w-full h-full object-cover" data-id="element-882" />
          </div>
        </div>
      </section>

      {/* Related Services */}
      <section className="py-16 bg-surface" data-id="element-883">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-884">
          <h2 className="text-2xl font-bold text-primary-dark mb-8" data-id="element-885">
            Related Services
          </h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-id="element-886">
            {[{
            title: 'Installation',
            icon: PenTool,
            link: '/services/installation'
          }, {
            title: 'Maintenance',
            icon: Wrench,
            link: '/services/maintenance'
          }, {
            title: 'TM44 Compliance',
            icon: ShieldCheck,
            link: '/services/tm44'
          }, {
            title: 'AC support for FM & property teams',
            icon: Settings,
            link: '/services/maintenance'
          }].map((service, index) => <Link key={index} to={service.link} className="bg-white p-6 rounded-2xl hover:shadow-md transition-shadow group flex flex-col items-center text-center" data-id="element-887">
                <service.icon className="w-8 h-8 text-accent mb-4 group-hover:scale-110 transition-transform" data-id="element-888" />
                <span className="font-semibold text-primary-dark" data-id="element-889">
                  {service.title}
                </span>
              </Link>)}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 bg-primary-dark text-white text-center" data-id="element-890">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-891">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-892">
            Planning a similar project?
          </h2>
          <p className="text-xl text-gray-300 mb-10" data-id="element-893">
            Our commercial team is ready to discuss your requirements and design
            a bespoke solution for your facility.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-894">
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-full text-white bg-accent hover:bg-accent/90 transition-colors" data-id="element-895">
              Book a site survey
              <ArrowRight className="ml-2 w-5 h-5" data-id="element-896" />
            </Link>
            <Link to="/contact" className="inline-flex items-center justify-center px-8 py-4 text-lg font-bold rounded-full text-white border-2 border-white/20 hover:bg-white/10 transition-colors" data-id="element-897">
              Discuss your project
            </Link>
          </div>
        </div>
      </section>
    </div>;
}